import type { FichaVitimaAtendimento } from './tipos'
import { preencherAchados } from './preencherAchados'

/**
 * Vítimas 2 a 8 do simulador de treinamento (`treinamento-veicular-vitimas`,
 * transcritas em `vitimas-tabletop.json`). As vítimas 1 e 9 têm ficha própria
 * em `vitimasCadastradas.ts`, com problemas e efeitos preparados.
 *
 * Transcrição didática: itens com "?" na fonte seguem como `incerto`, "estável"
 * no tardio significa que nada muda, e a cor de triagem de cada ficha é
 * sugestão derivada de Grave/Estável do roteiro, a confirmar pelo instrutor.
 */

export const VITIMA_2_BRACO_BANCO_COLUNA_B: FichaVitimaAtendimento = {
  id: 'vitima2',
  titulo: 'Vítima 2: Braço direito preso entre banco e coluna B',
  perfil: {
    papel: 'Carona',
    mecanismo: 'Colisão lateral; braço direito preso entre encosto e coluna B',
    descricao: 'Carona, consciente, braço direito preso entre encosto do banco e coluna B. Dor e ansiedade.',
    condicaoDescrita: 'Possível esmagamento leve/moderado e comprometimento neurovascular distal; sem fratura aparente',
    ator: 'Consciente, ansioso; dor 8/10; formigamento nos dedos direitos; pede para ser solto',
    aparencia: 'Braço pode estar edemaciado/arroxeado',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Braço pode estar edemaciado/arroxeado', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Consciente, ansioso; dor 8/10; formigamento nos dedos direitos; pede para ser solto', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Ausente', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia, falando; colar indicado pelo mecanismo', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '22/min, regular', leitura: '22 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '110 bpm, forte, regular', leitura: '110 bpm', estado: 'observado', regiao: 'membroSuperiorEsquerdo' },
    c_pulsos_especificos: { valor: 'Fraco, difícil palpar', estado: 'observado', regiao: 'membroSuperiorDireito' },
    c_pele_temperatura_umidade: { valor: 'Pálida por dor, quente, seca', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: 'Mão E <2 s; mão D lenta, >3 s', estado: 'observado' },
    c_pressao_arterial: { valor: '140/80 mmHg', leitura: '140/80 mmHg', estado: 'observado' },
    d_escala_glasgow: { valor: '15', leitura: 'Glasgow 15', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Alerta', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    e_exposicao_dorso: { valor: 'Avaliar braço direito; manter aquecido', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Crânio/face sem lesões; pescoço sem dor ou deformidade', estado: 'observado', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Estáveis e indolores', estado: 'observado', regiao: 'torax' },
    sec_membros_dorso: { valor: 'MS esquerdo, MMII e dorso sem lesões, PMS ok; MS direito preso, edema, distal frio/pálido, formigamento, dor 8/10, radial fraco/ausente?', estado: 'incerto' },
    sinais_spo2: { valor: '98%', leitura: '98%', estado: 'observado' },
    sinais_glicemia: { valor: '125 mg/dL', estado: 'observado' },
    sinais_temperatura: { valor: 'Normal (sem valor numérico)', estado: 'observado' },
    sample_sintomas: { valor: 'Dor 8/10 no braço direito, formigamento', estado: 'observado' },
    sample_alergias: { valor: 'Nega', estado: 'negativo' },
    sample_medicamentos: { valor: 'Nega', estado: 'negativo' },
    sample_passado_medico: { valor: 'Nega', estado: 'negativo' },
    sample_liquidos_alimentos: { valor: 'Almoço há 1 h', estado: 'observado' },
    sample_evento_trauma: { valor: 'Colisão lateral; braço ficou preso', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      a_permeabilidade_via_aerea: { valor: 'Pérvia', estado: 'observado' },
      b_frequencia_respiratoria: { valor: '20/min', leitura: '20 irpm', estado: 'observado', regiao: 'torax' },
      c_pulso_radial: { valor: '100 bpm', leitura: '100 bpm', estado: 'observado', regiao: 'membroSuperiorEsquerdo' },
      c_pulsos_especificos: { valor: 'Ausente? reavaliar após soltar', estado: 'incerto', regiao: 'membroSuperiorDireito' },
      c_pele_temperatura_umidade: { valor: 'Corada', estado: 'observado', regiao: 'corpoTodo' },
      c_perfusao_periferica: { valor: 'Mão D >5 s?', leitura: '> 5 s', estado: 'incerto' },
      c_pressao_arterial: { valor: '130/75 mmHg', leitura: '130/75 mmHg', estado: 'observado' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical', 'O2 (?)', 'Monitorização', 'Acesso venoso (?)', 'Analgesia (?)'],
  pendencias: ['Pulso/perfusão tardios incertos', 'Resposta após liberação não definida', 'Síndrome de esmagamento é hipótese didática'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'amarelo',
    justificativaTriagem: 'Classificação do roteiro de origem: Estável. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'urgente',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMA_3_TORAX_PNEUMOTORAX: FichaVitimaAtendimento = {
  id: 'vitima3',
  titulo: 'Vítima 3: Trauma torácico com piora respiratória',
  perfil: {
    papel: 'Ocupante de veículo',
    mecanismo: 'Colisão lateral, impacto no hemitórax direito',
    descricao: 'Pneumotórax Hipertensivo em desenvolvimento.',
    condicaoDescrita: 'Fratura de costela → pneumotórax → pneumotórax hipertensivo (hipótese do roteiro)',
    ator: 'Sentado, pânico, dispneia; dor torácica direita 9/10; confuso → sonolento',
    aparencia: 'Dispneico',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Dispneico', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Sentado, pânico, dispneia; dor torácica direita 9/10; confuso → sonolento', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Ausente', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia, frases curtas; considerar colar', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '30/min, superficial', leitura: '30 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Sons diminuídos à direita', estado: 'observado', regiao: 'torax' },
    b_expansao_toracica: { valor: 'Assimétrico, expansão direita reduzida', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '125 bpm, fraco', leitura: '125 bpm', estado: 'observado' },
    c_pele_temperatura_umidade: { valor: 'Pálida, fria', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: '>3 s', leitura: '> 3 s', estado: 'observado' },
    c_pressao_arterial: { valor: '100/60 mmHg', leitura: '100/60 mmHg', estado: 'observado' },
    c_estase_jugular: { valor: 'Planas', estado: 'observado', regiao: 'cabeca' },
    d_escala_glasgow: { valor: '15, ansioso', leitura: 'Glasgow 15', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Alerta', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    sinais_temperatura: { valor: 'Normal (sem valor)', estado: 'observado', regiao: 'corpoTodo' },
    e_exposicao_dorso: { valor: 'Expor tórax; manter aquecido', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Crânio/face sem lesões; traqueia centrada → desviada à esquerda; jugulares planas → túrgidas', estado: 'observado', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Dor à palpação de costelas direitas; crepitação? enfisema subcutâneo?; abdômen/pelve estáveis e indolores', estado: 'incerto', regiao: 'torax' },
    sec_membros_dorso: { valor: 'Sem lesões; PMS ok', estado: 'observado' },
    sinais_spo2: { valor: '91%', leitura: '91%', estado: 'observado' },
    sinais_glicemia: { valor: '120 mg/dL', estado: 'observado' },
    sinais_escala_dor: { valor: 'Torácica direita 9/10 com dispneia', leitura: '9/10', estado: 'observado' },
    sample_sintomas: { valor: 'Dor torácica direita e dispneia', estado: 'observado' },
    sample_alergias: { valor: 'Nega', estado: 'negativo' },
    sample_medicamentos: { valor: 'Nega', estado: 'negativo' },
    sample_passado_medico: { valor: 'Nega', estado: 'negativo' },
    sample_liquidos_alimentos: { valor: 'Jantar há 3 h', estado: 'observado' },
    sample_evento_trauma: { valor: 'Colisão lateral, bateu o tórax direito', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      a_permeabilidade_via_aerea: { valor: 'Pérvia', estado: 'observado' },
      b_frequencia_respiratoria: { valor: '38/min, muito superficial', leitura: '38 irpm', estado: 'observado', regiao: 'torax' },
      b_ausculta_pulmonar: { valor: 'Sons abolidos à direita', estado: 'observado', regiao: 'torax' },
      b_expansao_toracica: { valor: 'Assimetria evidente', estado: 'observado', regiao: 'torax' },
      c_pulso_radial: { valor: '140 bpm, filiforme', leitura: '140 bpm', estado: 'observado' },
      c_pele_temperatura_umidade: { valor: 'Cianose central', estado: 'observado', regiao: 'corpoTodo' },
      c_perfusao_periferica: { valor: '>5 s', leitura: '> 5 s', estado: 'observado' },
      c_pressao_arterial: { valor: '85/50 mmHg', leitura: '85/50 mmHg', estado: 'observado' },
      c_estase_jugular: { valor: 'Túrgidas', estado: 'observado', regiao: 'cabeca' },
      d_escala_glasgow: { valor: '13, confuso', leitura: 'Glasgow 13', estado: 'observado', regiao: 'cabeca' },
      d_nivel_avdi: { valor: 'Verbal', estado: 'observado', regiao: 'cabeca' },
      d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
      sinais_spo2: { valor: '86%', leitura: '86%', estado: 'observado' },
      sinais_escala_dor: { valor: 'Dispneia intensa', estado: 'observado' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical (?)', 'O2 alto fluxo', 'Acesso venoso', 'Descompressão torácica (?)'],
  pendencias: ['Validar hipótese e achados com interrogação', 'Não automatizar procedimento invasivo ou efeito associado sem validação'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'vermelho',
    justificativaTriagem: 'Classificação do roteiro de origem: Grave. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'imediata',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMA_4_BRACO_BANCO_TETO: FichaVitimaAtendimento = {
  id: 'vitima4',
  titulo: 'Vítima 4: Braço esquerdo preso entre banco e teto',
  perfil: {
    papel: 'Passageiro traseiro',
    mecanismo: 'Capotamento; braço esquerdo preso entre encosto do banco dianteiro e teto deformado',
    descricao: 'Passageiro traseiro, consciente, braço esquerdo preso entre encosto banco da frente e teto amassado. Ansioso.',
    condicaoDescrita: 'Possível compressão neurovascular do MS esquerdo, sem fratura óbvia',
    ator: 'Ansioso, dor 7/10; relata choques/dormência na mão',
    aparencia: 'Braço pode estar pálido/frio',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Braço pode estar pálido/frio', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Ansioso, dor 7/10; relata choques/dormência na mão', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Ausente', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia, falando; colar indicado', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '20/min, regular', leitura: '20 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '100 bpm, forte, regular', leitura: '100 bpm', estado: 'observado', regiao: 'membroSuperiorDireito' },
    c_pulsos_especificos: { valor: 'Presente, fraco', estado: 'observado', regiao: 'membroSuperiorEsquerdo', notaInstrutor: 'Tardio: estável; monitorar pulso esquerdo' },
    c_pele_temperatura_umidade: { valor: 'Corada, quente, seca', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: '>2 s', leitura: '> 2 s', estado: 'observado', regiao: 'membroSuperiorEsquerdo' },
    c_pressao_arterial: { valor: '130/80 mmHg', leitura: '130/80 mmHg', estado: 'observado' },
    d_escala_glasgow: { valor: '15', leitura: 'Glasgow 15', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Alerta', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    e_exposicao_dorso: { valor: 'Avaliar braço esquerdo; manter aquecido', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Sem lesões', estado: 'observado', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Estáveis e indolores', estado: 'observado', regiao: 'torax' },
    sec_membros_dorso: { valor: 'MS direito, MMII e dorso sem lesões, PMS ok; MS esquerdo preso, dor 7/10, radial fraco, perfusão >2 s, dormência/choque nos dedos, sem deformidade óbvia', estado: 'observado' },
    sinais_spo2: { valor: '99%', leitura: '99%', estado: 'observado' },
    sinais_glicemia: { valor: '110 mg/dL', estado: 'observado' },
    sinais_temperatura: { valor: 'Normal (sem valor)', estado: 'observado' },
    sample_sintomas: { valor: 'Dor 7/10 no braço esquerdo e dormência', estado: 'observado' },
    sample_alergias: { valor: 'Nega', estado: 'negativo' },
    sample_medicamentos: { valor: 'Nega', estado: 'negativo' },
    sample_passado_medico: { valor: 'Nega', estado: 'negativo' },
    sample_liquidos_alimentos: { valor: 'Almoço há 2 h', estado: 'observado' },
    sample_evento_trauma: { valor: 'Capotamento; braço preso', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      a_permeabilidade_via_aerea: { valor: 'Pérvia', estado: 'observado' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical', 'O2 (?)', 'Monitorização', 'Acesso venoso (?)'],
  pendencias: ['Estado tardio descrito apenas como estável; resposta após liberação não definida'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'amarelo',
    justificativaTriagem: 'Classificação do roteiro de origem: Estável. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'urgente',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMA_5_PERNA_PAINEL: FichaVitimaAtendimento = {
  id: 'vitima5',
  titulo: 'Vítima 5: Perna esquerda presa sob o painel',
  perfil: {
    papel: 'Motorista',
    mecanismo: 'Colisão frontal; perna esquerda presa sob painel deformado',
    descricao: 'Motorista consciente, perna esquerda presa sob o painel deformado. Dor moderada.',
    condicaoDescrita: 'Possível fratura fechada de fêmur ou tíbia/fíbula; risco de síndrome compartimental se houver demora',
    ator: 'Alerta e colaborativo; dor 6/10, pior ao mover; pode relatar pressão na perna',
    aparencia: 'Perna em posição não natural',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Perna em posição não natural', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Alerta e colaborativo; dor 6/10, pior ao mover; pode relatar pressão na perna', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Ausente', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia; colar indicado/obrigatório no roteiro', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '18/min, regular', leitura: '18 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '90 bpm, forte, regular', leitura: '90 bpm', estado: 'observado' },
    c_pulsos_especificos: { valor: 'Presente/palpável', estado: 'observado', regiao: 'membroInferiorEsquerdo', notaInstrutor: 'Tardio: estável; monitorar pé esquerdo' },
    c_pele_temperatura_umidade: { valor: 'Corada, quente, seca', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: '<2 s', leitura: '< 2 s', estado: 'observado', regiao: 'membroInferiorEsquerdo' },
    c_pressao_arterial: { valor: '130/70 mmHg', leitura: '130/70 mmHg', estado: 'observado' },
    d_escala_glasgow: { valor: '15', leitura: 'Glasgow 15', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Alerta', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    e_exposicao_dorso: { valor: 'Expor perna esquerda cuidadosamente; manter aquecido', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Sem lesões', estado: 'observado', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Marca do cinto no tórax, indolor; abdômen/pelve estáveis e indolores', estado: 'observado', regiao: 'torax' },
    sec_membros_dorso: { valor: 'MS e MID sem lesões, PMS ok; MIE preso sob painel (joelho/tíbia?), dor 6/10, edema/deformidade?, pulso pedioso presente e PMS ok; dorso sem lesões', estado: 'incerto' },
    sinais_spo2: { valor: '99%', leitura: '99%', estado: 'observado' },
    sinais_glicemia: { valor: '105 mg/dL', estado: 'observado' },
    sinais_temperatura: { valor: 'Normal (sem valor)', estado: 'observado' },
    sample_sintomas: { valor: 'Dor 6/10 na perna esquerda', estado: 'observado' },
    sample_alergias: { valor: 'Nega', estado: 'negativo' },
    sample_medicamentos: { valor: 'Nega', estado: 'negativo' },
    sample_passado_medico: { valor: 'Hipertensão controlada', estado: 'observado' },
    sample_liquidos_alimentos: { valor: 'Café há 1 h', estado: 'observado' },
    sample_evento_trauma: { valor: 'Colisão frontal, motorista, perna presa', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      a_permeabilidade_via_aerea: { valor: 'Pérvia', estado: 'observado' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical', 'O2 (?)', 'Monitorização', 'Acesso venoso (?)', 'Analgesia (?)'],
  pendencias: ['Local anatômico exato e fratura não definidos', 'Edema/deformidade incertos', 'Boa primeira ficha para testar avaliação e registro; não é classificação de triagem'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'amarelo',
    justificativaTriagem: 'Classificação do roteiro de origem: Estável. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'urgente',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMA_6_DEAMBULANDO_CONFUSA: FichaVitimaAtendimento = {
  id: 'vitima6',
  titulo: 'Vítima 6: Deambulando, confusa',
  perfil: {
    papel: 'Vítima encontrada na cena',
    mecanismo: 'Incerto: ejeção? capotamento?',
    descricao: 'Vítima encontrada andando pela cena, desorientada, com pequenas escoriações.',
    condicaoDescrita: 'Possível TCE leve/concussão; lesão de coluna a avaliar',
    ator: 'Caminha, parece perdido, repete perguntas, pode responder errado; obedece a comando para sentar',
    aparencia: 'Escoriações pequenas em rosto/braços; cefaleia leve',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Escoriações pequenas em rosto/braços; cefaleia leve', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Caminha, parece perdido, repete perguntas, pode responder errado; obedece a comando para sentar', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Ausente; apenas escoriações', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia, fala confusa; colar indicado no roteiro', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '18/min, regular', leitura: '18 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '95 bpm, forte, regular', leitura: '95 bpm', estado: 'observado' },
    c_pele_temperatura_umidade: { valor: 'Corada, quente, seca', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: '<2 s', leitura: '< 2 s', estado: 'observado' },
    c_pressao_arterial: { valor: '125/75 mmHg', leitura: '125/75 mmHg', estado: 'observado' },
    d_escala_glasgow: { valor: '14 (O4/V4/M6, confuso)', leitura: 'Glasgow 14', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Alerta/verbal', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    e_exposicao_dorso: { valor: 'Avaliar escoriações; manter aquecido', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Possível hematoma leve? sem deformidades; escoriações na face; pescoço sem dor à palpação cuidadosa (avaliar após colar, conforme texto)', estado: 'incerto', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Estáveis e indolores', estado: 'observado', regiao: 'torax' },
    sec_membros_dorso: { valor: 'Escoriações nos braços; MMII sem lesões, PMS ok; dorso sem dor/deformidade (com colar)', estado: 'observado' },
    sinais_spo2: { valor: '99%', leitura: '99%', estado: 'observado' },
    sinais_glicemia: { valor: '100 mg/dL', estado: 'observado' },
    sinais_temperatura: { valor: 'Normal (sem valor)', estado: 'observado' },
    sample_sintomas: { valor: 'Cefaleia leve; informação pouco confiável', estado: 'observado' },
    sample_alergias: { valor: 'Incerto', estado: 'observado' },
    sample_medicamentos: { valor: 'Incerto', estado: 'observado' },
    sample_passado_medico: { valor: 'Incerto', estado: 'observado' },
    sample_liquidos_alimentos: { valor: 'Incerto', estado: 'observado' },
    sample_evento_trauma: { valor: 'Não lembra; pergunta se estava no carro', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      x_hemorragia_externa: { valor: 'Ausente', estado: 'negativo' },
      a_permeabilidade_via_aerea: { valor: 'Pérvia', estado: 'observado' },
      d_escala_glasgow: { valor: 'Mantém Glasgow 14? melhora? (não definido)', estado: 'incerto' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical', 'Prancha (?)', 'O2 (?)', 'Monitorização'],
  pendencias: ['Mecanismo e evolução neurológica não definidos', 'Respostas do ator devem ser roteirizadas pelo instrutor'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'verde',
    justificativaTriagem: 'Classificação do roteiro de origem: Estável. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'estavel',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMA_7_AUTOEXTRACAO: FichaVitimaAtendimento = {
  id: 'vitima7',
  titulo: 'Vítima 7: Autoextração com dor no antebraço direito',
  perfil: {
    papel: 'Ocupante que se autoextraiu',
    mecanismo: 'Colisão; braço direito bateu no painel',
    descricao: 'Dor antebraço Direito, suspeita fratura rádio/ulna.',
    condicaoDescrita: 'Fratura fechada de antebraço direito, rádio/ulna possível',
    ator: 'Calmo, segura o braço, dor 7/10; segue instruções e pergunta pelos outros',
    aparencia: 'Dor no antebraço',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Dor no antebraço', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Calmo, segura o braço, dor 7/10; segue instruções e pergunta pelos outros', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Ausente', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia; considerar colar conforme mecanismo', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '20/min, regular', leitura: '20 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '100 bpm, forte, regular', leitura: '100 bpm', estado: 'observado' },
    c_pele_temperatura_umidade: { valor: 'Corada, quente, seca', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: '<2 s', leitura: '< 2 s', estado: 'observado' },
    c_pressao_arterial: { valor: '140/90 mmHg', leitura: '140/90 mmHg', estado: 'observado' },
    d_escala_glasgow: { valor: '15', leitura: 'Glasgow 15', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Alerta', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    e_exposicao_dorso: { valor: 'Expor membro afetado; manter aquecido', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Sem lesões', estado: 'observado', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Estáveis e indolores', estado: 'observado', regiao: 'torax' },
    sec_membros_dorso: { valor: 'MS direito doloroso, edema e possível deformidade?; pulso radial presente; PMS ok; demais membros/dorso sem lesões, PMS ok', estado: 'incerto' },
    sinais_spo2: { valor: '98%', leitura: '98%', estado: 'observado' },
    sinais_glicemia: { valor: '110 mg/dL', estado: 'observado' },
    sinais_temperatura: { valor: 'Normal (sem valor)', estado: 'observado' },
    sample_sintomas: { valor: 'Dor 7/10 no braço direito', estado: 'observado' },
    sample_alergias: { valor: 'Nega', estado: 'negativo' },
    sample_medicamentos: { valor: 'Nega', estado: 'negativo' },
    sample_passado_medico: { valor: 'Nega', estado: 'negativo' },
    sample_liquidos_alimentos: { valor: 'Café há 3 h', estado: 'observado' },
    sample_evento_trauma: { valor: 'Colisão; braço bateu no painel', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      a_permeabilidade_via_aerea: { valor: 'Pérvia', estado: 'observado' },
      b_frequencia_respiratoria: { valor: '18/min', leitura: '18 irpm', estado: 'observado', regiao: 'torax' },
      c_pulso_radial: { valor: '95 bpm', leitura: '95 bpm', estado: 'observado' },
      c_pressao_arterial: { valor: '135/85 mmHg', leitura: '135/85 mmHg', estado: 'observado' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical (?)', 'Imobilização do MS direito', 'Analgesia (?)'],
  pendencias: ['Osso/local exato não definido', 'Confirmar documentação da reavaliação distal'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'verde',
    justificativaTriagem: 'Classificação do roteiro de origem: Estável. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'estavel',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMA_8_ABDOMEN_CHOQUE: FichaVitimaAtendimento = {
  id: 'vitima8',
  titulo: 'Vítima 8: Trauma abdominal e deterioração',
  perfil: {
    papel: 'Motorista preso',
    mecanismo: 'Colisão frontal',
    descricao: 'Motorista preso, dor abdominal intensa. Deterioração rápida.',
    condicaoDescrita: 'Trauma abdominal fechado, possível lesão de órgão sólido (baço/fígado?) e choque hipovolêmico',
    ator: 'Geme de dor 10/10; consciente → confuso → responde à dor → inconsciente',
    aparencia: 'Pálido, sudoreico, taquipneico; abdômen distendido/rígido',
  },
  achadosIniciais: preencherAchados({
    chegada_queixa_aparencia: { valor: 'Pálido, sudoreico, taquipneico; abdômen distendido/rígido', estado: 'observado', regiao: 'corpoTodo' },
    chegada_instrucao_ator: { valor: 'Geme de dor 10/10; consciente → confuso → responde à dor → inconsciente', estado: 'observado' },
    x_hemorragia_externa: { valor: 'Sem hemorragia externa', estado: 'negativo' },
    a_permeabilidade_via_aerea: { valor: 'Pérvia, gemendo; colar indicado no roteiro', estado: 'observado' },
    b_frequencia_respiratoria: { valor: '30/min, superficial', leitura: '30 irpm', estado: 'observado', regiao: 'torax' },
    b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
    c_pulso_radial: { valor: '120 bpm, fino/rápido', leitura: '120 bpm', estado: 'observado' },
    c_pele_temperatura_umidade: { valor: 'Fria, pegajosa', estado: 'observado', regiao: 'corpoTodo' },
    c_perfusao_periferica: { valor: '>4 s', leitura: '> 4 s', estado: 'observado' },
    c_pressao_arterial: { valor: '90/60 mmHg', leitura: '90/60 mmHg', estado: 'observado' },
    d_escala_glasgow: { valor: '14 (O4/V4/M6, confuso)', leitura: 'Glasgow 14', estado: 'observado', regiao: 'cabeca' },
    d_nivel_avdi: { valor: 'Verbal', estado: 'observado', regiao: 'cabeca' },
    d_pupilas: { valor: 'Isocóricas', estado: 'observado', regiao: 'cabeca' },
    sinais_temperatura: { valor: '36.0 °C', leitura: '36.0 °C', estado: 'observado', regiao: 'corpoTodo' },
    e_exposicao_dorso: { valor: 'Expor tórax/abdômen doloroso; prevenir hipotermia', estado: 'observado', regiao: 'corpoTodo' },
    sec_cabeca_pescoco: { valor: 'Sem lesões; pescoço sem dor, traqueia centrada, jugulares planas', estado: 'observado', regiao: 'cabeca' },
    sec_torax_abdomen_pelve: { valor: 'Marca do cinto no tórax, indolor; abdômen distendido, rígido/em tábua, muito doloroso; pelve estável', estado: 'observado', regiao: 'torax' },
    sec_membros_dorso: { valor: 'Pulsos radiais/pediosos finos e rápidos; PMS inicialmente ok; MMII preso às ferragens?; dorso a verificar no rolamento se possível/indicado', estado: 'incerto' },
    sinais_spo2: { valor: '95%', leitura: '95%', estado: 'observado' },
    sinais_glicemia: { valor: '100 mg/dL', estado: 'observado' },
    sinais_escala_dor: { valor: 'Abdominal 10/10', leitura: '10/10', estado: 'observado' },
    sample_sintomas: { valor: 'Dor abdominal 10/10', estado: 'observado' },
    sample_alergias: { valor: 'Incerto', estado: 'observado' },
    sample_medicamentos: { valor: 'Incerto', estado: 'observado' },
    sample_passado_medico: { valor: 'Incerto', estado: 'observado' },
    sample_liquidos_alimentos: { valor: 'Incerto', estado: 'observado' },
    sample_evento_trauma: { valor: 'Colisão frontal, motorista', estado: 'observado' },
  }),
  estagios: [
    { id: 'tardio', rotulo: 'Tardio', achados: {
      x_hemorragia_externa: { valor: 'Não aplicável', estado: 'negativo' },
      a_permeabilidade_via_aerea: { valor: 'Pérvia, ruidosa por gemidos', estado: 'observado' },
      b_frequencia_respiratoria: { valor: '35/min, muito superficial', leitura: '35 irpm', estado: 'observado', regiao: 'torax' },
      b_ausculta_pulmonar: { valor: 'Murmúrio vesicular presente bilateralmente', estado: 'observado', regiao: 'torax' },
      c_pulso_radial: { valor: '140 bpm, filiforme', leitura: '140 bpm', estado: 'observado' },
      c_pele_temperatura_umidade: { valor: 'Fria, pegajosa, cianose nas extremidades', estado: 'observado', regiao: 'corpoTodo' },
      c_perfusao_periferica: { valor: '>5 s', leitura: '> 5 s', estado: 'observado' },
      c_pressao_arterial: { valor: '70/palpável', estado: 'observado' },
      d_escala_glasgow: { valor: '8 (O2/V2/M4, resposta à dor)', leitura: 'Glasgow 8', estado: 'observado', regiao: 'cabeca' },
      d_nivel_avdi: { valor: 'Dor', estado: 'observado', regiao: 'cabeca' },
      d_pupilas: { valor: 'Midriáticas, lentas', estado: 'observado', regiao: 'cabeca' },
      sinais_temperatura: { valor: '35.5 °C', leitura: '35.5 °C', estado: 'observado', regiao: 'corpoTodo' },
      e_exposicao_dorso: { valor: 'Muito distendido', estado: 'observado', regiao: 'abdomen' },
      sinais_spo2: { valor: '88%', leitura: '88%', estado: 'observado' },
      sinais_escala_dor: { valor: 'Resposta à dor', estado: 'observado' },
    } },
  ],
  intervencoesCitadas: ['Colar cervical', 'O2 alto fluxo', 'Dois acessos calibrosos', 'Reposição volêmica (?)', 'Manta térmica', 'Extricação rápida (?)'],
  pendencias: ['Órgão lesionado incerto', 'Classe de choque consta no resumo, requer validação', 'Aprisionamento de MMII incerto', 'Não usar evolução como efeito causal automático'],
  referenciaConteudo: { fonte: 'treinamento-veicular-vitimas (index.html / vitimas-tabletop.json)', versao: 'csv-lab.tabletop.vitimas.v1' },
  gabaritoInstrutor: {
    triagemSugerida: 'vermelho',
    justificativaTriagem: 'Classificação do roteiro de origem: Grave. A cor de triagem é sugestão a confirmar pelo instrutor.',
    prioridadeExtracao: 'imediata',
    rotasRecomendadas: [],
    notasDebriefing: [],
  },
}

export const VITIMAS_TREINAMENTO: readonly FichaVitimaAtendimento[] = [
  VITIMA_2_BRACO_BANCO_COLUNA_B,
  VITIMA_3_TORAX_PNEUMOTORAX,
  VITIMA_4_BRACO_BANCO_TETO,
  VITIMA_5_PERNA_PAINEL,
  VITIMA_6_DEAMBULANDO_CONFUSA,
  VITIMA_7_AUTOEXTRACAO,
  VITIMA_8_ABDOMEN_CHOQUE,
]
